import { getWorkspaceId } from '../../lib/auth-storage';
import { useCallback, useEffect, useState } from 'react';
import {
  aiCreativeApi,
  BULK_ACTIONS,
  type ActivityEntry,
  type ApprovalWorkflow,
  type AssetComparison,
  type AssetGridRow,
  type CreativeAsset,
  type CreativeNotification,
  type DashboardSummary,
  type QualityExplanation,
} from '../../api/aicreative';

const WORKSPACE = (getWorkspaceId() ?? '');

export function CreativeStudioDashboardPage() {
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [rows, setRows] = useState<AssetGridRow[]>([]);
  const [total, setTotal] = useState(0);
  const [notifications, setNotifications] = useState<CreativeNotification[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [asset, setAsset] = useState<CreativeAsset | null>(null);
  const [comparisons, setComparisons] = useState<AssetComparison[]>([]);
  const [explanation, setExplanation] = useState<QualityExplanation | null>(null);
  const [workflow, setWorkflow] = useState<ApprovalWorkflow | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, grid, act, notifs] = await Promise.all([
        aiCreativeApi.dashboard(WORKSPACE),
        aiCreativeApi.assetGrid(WORKSPACE),
        aiCreativeApi.listActivity(undefined, 20),
        aiCreativeApi.listNotifications(true),
      ]);
      setDashboard(dash);
      setRows(grid.rows);
      setTotal(grid.total);
      setActivity(act);
      setNotifications(notifs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load creative studio');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createAndGenerate = async () => {
    setBusy(true);
    try {
      const a = await aiCreativeApi.createAsset({
        workspace_id: WORKSPACE,
        name: 'Demo Main Image',
        type: 'main_image',
        asin: 'B000DEMO02',
        marketplace_id: 'ATVPDKIKX0DER',
      });
      await aiCreativeApi.generate(a.id, 'Professional product photo on pure white background, studio lighting');
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const loadAsset = async (id: string) => {
    setSelectedId(id);
    try {
      const [a, cmp, exp, wf] = await Promise.all([
        aiCreativeApi.getAsset(id),
        aiCreativeApi.compare(id),
        aiCreativeApi.explain(id),
        aiCreativeApi.workflow(id),
      ]);
      setAsset(a);
      setComparisons(cmp);
      setExplanation(exp);
      setWorkflow(wf);
    } catch {
      setAsset(null);
    }
  };

  const approveAsset = async () => {
    if (!selectedId) return;
    await aiCreativeApi.transition(selectedId, 'approved');
    loadAsset(selectedId);
    refresh();
  };

  const submitForReview = async () => {
    if (!selectedId) return;
    await aiCreativeApi.transition(selectedId, 'under_review');
    loadAsset(selectedId);
    refresh();
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runBulk = async (action: string) => {
    if (selectedIds.size === 0) return;
    setBusy(true);
    try {
      await aiCreativeApi.triggerBulk({ action, asset_ids: [...selectedIds], workspace_id: WORKSPACE });
      setSelectedIds(new Set());
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const runSearch = async () => {
    if (!searchText.trim()) return;
    setBusy(true);
    try {
      await aiCreativeApi.globalSearch(searchText, WORKSPACE);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">AI Creative Studio</h1>
          <p className="mt-1 text-sm text-slate-600">
            Enterprise DAM and AI-powered creative generation with brand validation, quality scoring, and approval workflows
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={createAndGenerate}
            disabled={busy}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            Generate Sample Asset
          </button>
          <button
            type="button"
            onClick={refresh}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50"
          >
            Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{error}</div>
      )}

      {dashboard && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          {[
            { label: 'Total Assets', value: dashboard.total_assets },
            { label: 'Pending Review', value: dashboard.pending_review },
            { label: 'Approved', value: dashboard.approved_assets },
            { label: 'Published', value: dashboard.published_assets },
            { label: 'Avg Quality', value: `${Math.round(dashboard.avg_quality_score * 100)}%` },
            { label: 'Notifications', value: dashboard.unread_notifications },
          ].map((c) => (
            <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase text-slate-400">{c.label}</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {workflow && (
        <section className="rounded-xl border border-violet-200 bg-violet-50/50 p-4">
          <h2 className="font-semibold text-violet-900">Approval Workflow</h2>
          <div className="mt-2 flex flex-wrap gap-3 text-sm text-violet-800">
            {workflow.steps.map((s) => (
              <span key={s.name} className="rounded-full bg-white/80 px-3 py-1 capitalize">
                {s.name.replace(/_/g, ' ')}: {s.state}
              </span>
            ))}
            {workflow.publish_ready && <span className="font-semibold text-violet-700">Publish Ready</span>}
          </div>
        </section>
      )}

      {notifications.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-semibold">Notifications ({notifications.length})</h2>
          <ul className="mt-2 space-y-1 text-sm text-slate-600">
            {notifications.slice(0, 5).map((n) => (
              <li key={n.id}>
                <strong>{n.title}</strong> — {n.body}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          type="search"
          placeholder="Search assets, ASINs, types…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          className="min-w-[240px] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
        <button type="button" disabled={busy} onClick={runSearch} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
          Search
        </button>
        {BULK_ACTIONS.map((action) => (
          <button
            key={action}
            type="button"
            disabled={busy || selectedIds.size === 0}
            onClick={() => runBulk(action)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium capitalize disabled:opacity-40"
          >
            Bulk {action}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2 space-y-4">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3 flex justify-between">
              <h2 className="font-semibold">Digital Asset Manager</h2>
              <span className="text-sm text-slate-500">{total} assets</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 w-8" />
                    <th className="px-3 py-2">Asset</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Quality</th>
                    <th className="px-3 py-2">Brand</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => (
                    <tr
                      key={row.asset_id}
                      className={`cursor-pointer hover:bg-slate-50 ${selectedId === row.asset_id ? 'bg-brand-50' : ''}`}
                      onClick={() => loadAsset(row.asset_id)}
                    >
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(row.asset_id)} onChange={() => toggleSelect(row.asset_id)} />
                      </td>
                      <td className="px-3 py-2">
                        <p className="font-medium">{row.name}</p>
                        <p className="text-xs text-slate-500 font-mono">{row.asin ?? row.asset_id.slice(0, 8)}</p>
                      </td>
                      <td className="px-3 py-2 capitalize">{row.type.replace(/_/g, ' ')}</td>
                      <td className="px-3 py-2">{Math.round(row.quality_score * 100)}%</td>
                      <td className="px-3 py-2">{row.brand_aligned ? '✓' : '—'}</td>
                      <td className="px-3 py-2 capitalize">{row.status.replace(/_/g, ' ')}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                        No assets yet. Click &quot;Generate Sample Asset&quot; to start.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {comparisons.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="font-semibold">Comparison Workspace</h2>
              <div className="mt-3 space-y-3">
                {comparisons.map((c) => (
                  <div key={`${c.field}-${c.version}`} className="rounded-lg border border-slate-100 p-3 text-sm">
                    <p className="font-medium capitalize">{c.field.replace(/_/g, ' ')} · v{c.version}</p>
                    <div className="mt-2 grid gap-2 md:grid-cols-2">
                      <div className="rounded bg-slate-50 p-2">
                        <p className="text-xs text-slate-600 font-semibold">Previous</p>
                        <p className="text-slate-700 truncate">{c.previous || '—'}</p>
                      </div>
                      <div className="rounded bg-violet-50 p-2">
                        <p className="text-xs text-violet-600 font-semibold">Current</p>
                        <p className="text-slate-700 truncate">{c.current || '—'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="space-y-4">
          {asset ? (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
              <h2 className="font-semibold">Generation Workspace</h2>
              <p className="text-sm font-medium">{asset.name}</p>
              <dl className="grid grid-cols-2 gap-2 text-sm">
                <dt className="text-slate-500">Quality</dt>
                <dd>{Math.round(asset.quality.overall_score * 100)}%</dd>
                <dt className="text-slate-500">Brand</dt>
                <dd>{asset.validation.brand_consistent ? 'Aligned' : 'Review'}</dd>
                <dt className="text-slate-500">Marketplace</dt>
                <dd>{asset.validation.marketplace_compliance_ok ? 'Compliant' : 'Issues'}</dd>
                <dt className="text-slate-500">Provider</dt>
                <dd className="text-xs">{asset.provider ?? '—'}</dd>
              </dl>
              {explanation && (
                <div className="rounded-lg bg-slate-50 p-3 text-xs">
                  <p className="font-medium">Quality Explainability</p>
                  <p className="mt-1 text-slate-600">{explanation.summary}</p>
                  {explanation.factors && (
                    <ul className="mt-2 space-y-1">
                      {explanation.factors.slice(0, 4).map((f) => (
                        <li key={f.factor}>{f.factor}: {Math.round(f.score * 100)}%</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                {asset.status === 'generated' && (
                  <button type="button" onClick={submitForReview} className="text-sm text-brand-600 hover:underline">
                    Submit for Review
                  </button>
                )}
                {(asset.status === 'under_review' || asset.status === 'generated') && (
                  <button type="button" onClick={approveAsset} className="text-sm text-emerald-600 hover:underline">
                    Approve
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
              Select an asset to view quality validation, explainability, and approval actions
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="font-semibold">Creative History</h2>
            <ul className="mt-2 max-h-64 space-y-2 overflow-y-auto text-xs text-slate-600">
              {activity.map((a) => (
                <li key={a.id}>
                  <span className="text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
                  <br />
                  {a.message}
                </li>
              ))}
              {activity.length === 0 && <li>No recent activity</li>}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
